import React, { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Mail,
  Plus,
  Trash2,
  CheckCircle,
  Clock,
  Shield,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

export default function AdminEmails() {
  const { isAdmin } = useAuth();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    role: 'archer',
    notes: ''
  });

  useEffect(() => {
    if (isAdmin) {
      fetchEmails();
    }
  }, [isAdmin]);

  const fetchEmails = async () => {
    try {
      const response = await adminAPI.getAuthorizedEmails();
      setEmails(response.data);
    } catch (error) {
      toast.error('Failed to load authorized emails');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }

    try {
      await adminAPI.addAuthorizedEmail(formData);
      toast.success('Email authorized successfully');
      setDialogOpen(false);
      setFormData({ email: '', role: 'archer', notes: '' });
      fetchEmails();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to authorize email';
      toast.error(message);
    }
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm('Remove this authorized email?')) return;

    try {
      await adminAPI.removeAuthorizedEmail(emailId);
      setEmails(emails.filter((e) => e.id !== emailId));
      toast.success('Email removed');
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const pendingCount = emails.filter((e) => !e.is_registered).length;
  const registeredCount = emails.filter((e) => e.is_registered).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-emails-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Authorized Emails</h1>
          <p className="text-slate-600 mt-1">
            {pendingCount} pending, {registeredCount} registered
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-email-btn">
              <Plus className="w-4 h-4 mr-2" />
              Authorize Email
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Authorize New Email</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="auth-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="auth-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="archer">Archer</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Any notes about this user..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="auth-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="btn-primary" onClick={handleAdd} data-testid="confirm-auth-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Authorize
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-blue-800 font-medium">
                Pre-authorization required for registration
              </p>
              <p className="text-sm text-blue-600 mt-1">
                Users can only create accounts if their email has been authorized here first.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Emails List */}
      {emails.length > 0 ? (
        <div className="space-y-3">
          {emails.map((email) => (
            <Card
              key={email.id}
              className="card-base"
              data-testid={`auth-email-${email.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      email.is_registered ? 'bg-emerald-100' : 'bg-amber-100'
                    }`}>
                      <Mail className={`w-5 h-5 ${
                        email.is_registered ? 'text-emerald-600' : 'text-amber-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{email.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline">{email.role}</Badge>
                        {email.is_registered ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Registered
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </div>
                      {email.notes && (
                        <p className="text-sm text-slate-500 mt-1">{email.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      Added {formatDate(email.created_at)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => handleDelete(email.id)}
                      data-testid={`delete-auth-${email.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <Mail className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No authorized emails</h3>
            <p className="text-slate-500 mb-4">
              Add email addresses to allow users to register
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
